import React, { ElementType } from 'react';
import {
  Box,
  Button,
  HStack,
  IconButton,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { FiArrowUpRight, FiImage } from 'react-icons/fi';
import { CatalogueItem } from '../../utils/types';
import {
  CatalogueEmptyState,
  CatalogueStatusBadge,
  catalogueBorder,
  catalogueSurface,
  formatCurrency,
  formatDateLabel,
} from './components/catalogueUi';

type Props = {
  products: CatalogueItem[];
  onOpenItem: (itemId: string) => void;
  onEditItem: (itemId: string) => void;
  onDeleteItem: (item: CatalogueItem) => void;
};

const CatalogueCards: React.FC<Props> = ({
  products,
  onDeleteItem,
  onEditItem,
  onOpenItem,
}) => {
  if (!products.length) {
    return (
      <CatalogueEmptyState
        title="No catalogue items found"
        description="Try clearing your filters or add a new item to start building your catalogue."
        icon={FiImage as ElementType}
      />
    );
  }

  return (
    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
      {products.map((product) => (
        <Box
          key={product.id}
          bg={catalogueSurface}
          borderWidth="1px"
          borderColor={catalogueBorder}
          borderRadius="24px"
          overflow="hidden"
          boxShadow="0 14px 36px rgba(15, 23, 42, 0.05)"
        >
          <Box
            h="220px"
            bg="gray.100"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            onClick={() => onOpenItem(product.id)}
          >
            {product.images?.[0] ? (
              <img
                src={product.images[0].image}
                alt={product.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Box as={FiImage as ElementType} boxSize={10} color="gray.400" />
            )}
          </Box>
          <Stack spacing={4} p={4}>
            <VStack align="flex-start" spacing={2}>
              <HStack justify="space-between" w="full" align="flex-start">
                <Text
                  fontWeight="800"
                  fontSize="lg"
                  color="gray.900"
                  noOfLines={2}
                  cursor="pointer"
                  onClick={() => onOpenItem(product.id)}
                >
                  {product.name}
                </Text>
                <CatalogueStatusBadge
                  isActive={Boolean(product.is_active)}
                  imageCount={product.images?.length}
                />
              </HStack>
              <Text fontSize="2xl" fontWeight="800" color="gray.900">
                {formatCurrency(product.price)}
              </Text>
              <Text fontSize="sm" color="gray.600" noOfLines={3}>
                {product.description || 'No description provided yet.'}
              </Text>
            </VStack>

            <HStack justify="space-between" color="gray.500" fontSize="sm">
              <Text>
                {product.images?.length
                  ? `${product.images.length} image${product.images.length > 1 ? 's' : ''}`
                  : 'No images'}
              </Text>
              <Text>{formatDateLabel(product.updated_at || product.created_at)}</Text>
            </HStack>

            <HStack spacing={2}>
              <Button
                flex="1"
                rightIcon={<Box as={FiArrowUpRight as ElementType} />}
                borderRadius="16px"
                onClick={() => onOpenItem(product.id)}
              >
                Open
              </Button>
              <IconButton
                aria-label="Edit item"
                icon={<EditIcon />}
                borderRadius="16px"
                onClick={() => onEditItem(product.id)}
              />
              <IconButton
                aria-label="Delete item"
                icon={<DeleteIcon />}
                borderRadius="16px"
                variant="ghost"
                colorScheme="red"
                onClick={() => onDeleteItem(product)}
              />
            </HStack>
          </Stack>
        </Box>
      ))}
    </SimpleGrid>
  );
};

export default CatalogueCards;
